'use client';

import { useState, useEffect } from 'react';
import {
	KeyIcon,
	EyeIcon,
	EyeSlashIcon,
	ClipboardDocumentIcon,
	CheckIcon,
	ExclamationTriangleIcon,
	ShieldCheckIcon,
	PlayIcon,
	ArrowPathIcon,
} from '@heroicons/react/24/outline';
import { apiService } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import LoadingSpinner from '../components/LoadingSpinner';
import Button from '../components/button';
import { toast } from 'sonner';
import { format } from 'date-fns';

const ApiKeys = () => {
	const [loading, setLoading] = useState(true);
	const [keysData, setKeysData] = useState(null);
	const [usage, setUsage] = useState(null);
	const [showKeys, setShowKeys] = useState({
		test: false,
		live: false,
	});
	const [actionLoading, setActionLoading] = useState({
		resetTest: false,
		activateLive: false,
		resetLive: false,
	});
	const [copiedKey, setCopiedKey] = useState(null);
	const [confirmationCode, setConfirmationCode] = useState('');
	const [showResetLiveModal, setShowResetLiveModal] = useState(false);

	const { user } = useAuth();

	useEffect(() => {
		fetchKeysData();
		fetchUsage();
	}, []);

	const fetchKeysData = async () => {
		try {
			setLoading(true);
			const response = await apiService.getInstitutionKeys();
			if (response.data.status === 200) {
				setKeysData(response.data.data);
			}
		} catch (error) {
			console.error('Error fetching API keys:', error);
			toast.error('Failed to fetch API keys');
		} finally {
			setLoading(false);
		}
	};

	const fetchUsage = async () => {
		try {
			const response = await apiService.getKeysUsage();
			if (response.data.status === 200) {
				setUsage(response.data.data);
			}
		} catch (error) {
			console.error('Error fetching usage:', error);
		}
	};

	const handleCopyKey = async (key, environment) => {
		try {
			await navigator.clipboard.writeText(key);
			setCopiedKey(`${environment}-${key}`);
			toast.success(`${environment} API key copied to clipboard`);
			setTimeout(() => setCopiedKey(null), 2000);
		} catch (error) {
			toast.error('Failed to copy to clipboard');
		}
	};

	const handleResetTestKeys = async () => {
		if (
			!window.confirm(
				'Are you sure you want to reset your test API keys? This will invalidate your current test keys.',
			)
		) {
			return;
		}

		setActionLoading((prev) => ({ ...prev, resetTest: true }));
		try {
			const response = await apiService.resetTestKeys();
			if (response.data.status === 200) {
				toast.success('Test API keys reset successfully!');
				fetchKeysData();
				fetchUsage();
			}
		} catch (error) {
			const message =
				error.response?.data?.message ||
				error.message ||
				'Failed to reset test keys';
			toast.error(message);
		} finally {
			setActionLoading((prev) => ({ ...prev, resetTest: false }));
		}
	};

	const handleActivateLiveKeys = async () => {
		if (
			!window.confirm(
				'Are you sure you want to activate live API keys? This will enable production access for your institution.',
			)
		) {
			return;
		}

		setActionLoading((prev) => ({ ...prev, activateLive: true }));
		try {
			const response = await apiService.activateLiveKeys();
			if (response.data.status === 200) {
				toast.success('Live API keys activated successfully!');
				fetchKeysData();
				fetchUsage();
			}
		} catch (error) {
			const message =
				error.response?.data?.message ||
				error.message ||
				'Failed to activate live keys';
			toast.error(message);
		} finally {
			setActionLoading((prev) => ({ ...prev, activateLive: false }));
		}
	};

	const handleResetLiveKeys = async () => {
		if (!confirmationCode || confirmationCode !== user?.id?.toString()) {
			toast.error('Please provide your institution ID as confirmation');
			return;
		}

		setActionLoading((prev) => ({ ...prev, resetLive: true }));
		try {
			const response = await apiService.resetLiveKeys(confirmationCode);
			if (response.data.status === 200) {
				toast.success('Live API keys reset successfully!');
				setShowResetLiveModal(false);
				setConfirmationCode('');
				fetchKeysData();
				fetchUsage();
			}
		} catch (error) {
			const message =
				error.response?.data?.message ||
				error.message ||
				'Failed to reset live keys';
			toast.error(message);
		} finally {
			setActionLoading((prev) => ({ ...prev, resetLive: false }));
		}
	};

	const toggleKeyVisibility = (environment) => {
		setShowKeys((prev) => ({
			...prev,
			[environment]: !prev[environment],
		}));
	};

	const maskKey = (key) => {
		if (!key) return 'Not available';
		// Show "kai_test_" (9 chars) or "kai_live_" (9 chars)
		const prefix = key.substring(0, 9);
		const suffix = key.slice(-4);
		return `${prefix}${'•'.repeat(20)}${suffix}`;
	};

	if (loading) {
		return (
			<div className="flex items-center justify-center h-64">
				<LoadingSpinner size="lg" />
			</div>
		);
	}

	return (
		<div className="space-y-6">
			{/* Page description */}
			<div>
				<p className="text-sm text-gray-500">
					Manage your API keys for test and production environments
				</p>
			</div>

			{/* Environment Keys Grid */}
			<div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
				{/* Test Environment */}
				<div className="card border-l-4 border-l-blue-500">
					<div className="flex items-center justify-between mb-4">
						<div className="flex items-center">
							<div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center mr-3">
								<KeyIcon className="h-6 w-6 text-blue-600" />
							</div>
							<div>
								<h3 className="text-lg font-medium text-gray-900">
									Test Environment
								</h3>
								<p className="text-sm text-gray-500">
									For development and testing
								</p>
							</div>
						</div>
						<span className="status-badge status-pending">Sandbox</span>
					</div>

					<div className="space-y-4">
						{/* Test API Key */}
						<div>
							<label className="block text-sm font-medium text-gray-700 mb-2">
								Test API Key
							</label>
							<div className="flex items-center space-x-2">
								<code className="flex-1 bg-gray-100 px-3 py-2 rounded text-sm font-mono overflow-hidden">
									{showKeys.test
										? keysData?.test?.apiKey || 'Not available'
										: maskKey(keysData?.test?.apiKey)}
								</code>
								<button
									onClick={() => toggleKeyVisibility('test')}
									className="p-2 text-gray-400 hover:text-gray-600 transition-colors">
									{showKeys.test ? (
										<EyeSlashIcon className="h-4 w-4" />
									) : (
										<EyeIcon className="h-4 w-4" />
									)}
								</button>
								<button
									onClick={() =>
										handleCopyKey(keysData?.test?.apiKey, 'Test')
									}
									className="p-2 text-gray-400 hover:text-gray-600 transition-colors">
									{copiedKey === `Test-${keysData?.test?.apiKey}` ? (
										<CheckIcon className="h-4 w-4 text-green-600" />
									) : (
										<ClipboardDocumentIcon className="h-4 w-4" />
									)}
								</button>
							</div>
						</div>

						{/* Test Key Stats */}
						<div className="grid grid-cols-2 gap-4 pt-2">
							<div>
								<p className="text-xs text-gray-500">Usage Count</p>
								<p className="text-sm font-medium text-gray-900">
									{usage?.test?.usageCount?.toLocaleString() || '0'}
								</p>
							</div>
							<div>
								<p className="text-xs text-gray-500">Last Reset</p>
								<p className="text-sm font-medium text-gray-900">
									{keysData?.test?.lastReset
										? format(
												new Date(keysData.test.lastReset),
												'MMM dd, yyyy',
										  )
										: 'Never'}
								</p>
							</div>
						</div>

						{/* Reset Test Keys Button */}
						<div className="pt-4 border-t border-gray-200">
							<Button
								type="danger"
								variant="outlined"
								label="Reset Test Keys"
								iconLeft={<ArrowPathIcon className="h-4 w-4" />}
								onClick={handleResetTestKeys}
								loading={actionLoading.resetTest}
								disabled={actionLoading.resetTest}
								size="S"
							/>
						</div>
					</div>
				</div>

				{/* Live Environment */}
				<div className="card border-l-4 border-l-green-500">
					<div className="flex items-center justify-between mb-4">
						<div className="flex items-center">
							<div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center mr-3">
								<ShieldCheckIcon className="h-6 w-6 text-green-600" />
							</div>
							<div>
								<h3 className="text-lg font-medium text-gray-900">
									Live Environment
								</h3>
								<p className="text-sm text-gray-500">
									For production use
								</p>
							</div>
						</div>
						<span
							className={`status-badge ${
								keysData?.live?.activatedAt
									? 'status-paid'
									: 'status-pending'
							}`}>
							{keysData?.live?.activatedAt ? 'Active' : 'Inactive'}
						</span>
					</div>

					<div className="space-y-4">
						{/* Live API Key */}
						<div>
							<label className="block text-sm font-medium text-gray-700 mb-2">
								Live API Key
							</label>
							{keysData?.live?.activatedAt ? (
								<div className="flex items-center space-x-2">
									<code className="flex-1 bg-gray-100 px-3 py-2 rounded text-sm font-mono overflow-hidden">
										{showKeys.live
											? keysData?.live?.apiKey || 'Not available'
											: maskKey(keysData?.live?.apiKey)}
									</code>
									<button
										onClick={() => toggleKeyVisibility('live')}
										className="p-2 text-gray-400 hover:text-gray-600 transition-colors">
										{showKeys.live ? (
											<EyeSlashIcon className="h-4 w-4" />
										) : (
											<EyeIcon className="h-4 w-4" />
										)}
									</button>
									<button
										onClick={() =>
											handleCopyKey(keysData?.live?.apiKey, 'Live')
										}
										className="p-2 text-gray-400 hover:text-gray-600 transition-colors">
										{copiedKey === `Live-${keysData?.live?.apiKey}` ? (
											<CheckIcon className="h-4 w-4 text-green-600" />
										) : (
											<ClipboardDocumentIcon className="h-4 w-4" />
										)}
									</button>
								</div>
							) : (
								<div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
									<div className="flex">
										<ExclamationTriangleIcon className="h-5 w-5 text-yellow-400 mr-2" />
										<div>
											<h4 className="text-sm font-medium text-yellow-800">
												Live Keys Not Activated
											</h4>
											<p className="text-sm text-yellow-700 mt-1">
												You need to activate your live API keys to use them in
												production.
											</p>
										</div>
									</div>
								</div>
							)}
						</div>

						{/* Live Key Stats */}
						{keysData?.live?.activatedAt && (
							<div className="grid grid-cols-2 gap-4 pt-2">
								<div>
									<p className="text-xs text-gray-500">Usage Count</p>
									<p className="text-sm font-medium text-gray-900">
										{usage?.live?.usageCount?.toLocaleString() || '0'}
									</p>
								</div>
								<div>
									<p className="text-xs text-gray-500">Activated</p>
									<p className="text-sm font-medium text-gray-900">
										{format(
											new Date(keysData.live.activatedAt),
											'MMM dd, yyyy',
										)}
									</p>
								</div>
							</div>
						)}

						{/* Live Keys Actions */}
						<div className="pt-4 border-t border-gray-200 space-y-2">
							{!keysData?.live?.activatedAt ? (
								<Button
									type="primary"
									label="Activate Live Keys"
									iconLeft={<PlayIcon className="h-4 w-4" />}
									onClick={handleActivateLiveKeys}
									loading={actionLoading.activateLive}
									disabled={actionLoading.activateLive}
									size="S"
								/>
							) : (
								<Button
									type="danger"
									variant="outlined"
									label="Reset Live Keys"
									iconLeft={<ArrowPathIcon className="h-4 w-4" />}
									onClick={() => setShowResetLiveModal(true)}
									size="S"
								/>
							)}
						</div>
					</div>
				</div>
			</div>

			{/* Usage Statistics */}
			<div className="card">
				<h3 className="text-lg font-medium text-gray-900 mb-4">
					Usage Statistics
				</h3>
				<div className="grid grid-cols-1 md:grid-cols-3 gap-4">
					<div className="bg-blue-50 rounded-lg p-4">
						<div className="flex items-center">
							<KeyIcon className="h-8 w-8 text-blue-600" />
							<div className="ml-3">
								<p className="text-sm font-medium text-blue-900">
									Test Requests
								</p>
								<p className="text-2xl font-bold text-blue-600">
									{usage?.test?.usageCount?.toLocaleString() || '0'}
								</p>
							</div>
						</div>
					</div>
					<div className="bg-green-50 rounded-lg p-4">
						<div className="flex items-center">
							<ShieldCheckIcon className="h-8 w-8 text-green-600" />
							<div className="ml-3">
								<p className="text-sm font-medium text-green-900">
									Live Requests
								</p>
								<p className="text-2xl font-bold text-green-600">
									{usage?.live?.usageCount?.toLocaleString() || '0'}
								</p>
							</div>
						</div>
					</div>
					<div className="bg-gray-50 rounded-lg p-4">
						<div className="flex items-center">
							<div className="h-8 w-8 bg-gray-600 rounded-lg flex items-center justify-center">
								<span className="text-white font-bold text-sm">T</span>
							</div>
							<div className="ml-3">
								<p className="text-sm font-medium text-gray-900">
									Total Requests
								</p>
								<p className="text-2xl font-bold text-gray-600">
									{(
										(usage?.test?.usageCount || 0) +
										(usage?.live?.usageCount || 0)
									).toLocaleString()}
								</p>
							</div>
						</div>
					</div>
				</div>
			</div>

			{/* API Security Information */}
			<div className="card bg-blue-50 border-blue-200">
				<div className="flex">
					<div className="flex-shrink-0">
						<ShieldCheckIcon className="h-5 w-5 text-blue-400" />
					</div>
					<div className="ml-3">
						<h3 className="text-sm font-medium text-blue-800">
							API Key Security Best Practices
						</h3>
						<div className="mt-2 text-sm text-blue-700">
							<ul className="list-disc pl-5 space-y-1">
								<li>
									Your API keys start with <code>kai_test_</code> for test
									environment and <code>kai_live_</code> for production
								</li>
								<li>
									Never share your API keys publicly or commit them to version
									control
								</li>
								<li>
									Use test keys for development and live keys only for
									production
								</li>
								<li>
									Reset your keys immediately if you suspect they have been
									compromised
								</li>
								<li>
									Monitor your API usage regularly for any suspicious activity
								</li>
							</ul>
						</div>
					</div>
				</div>
			</div>

			{/* Reset Live Keys Modal */}
			{showResetLiveModal && (
				<div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
					<div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
						<div className="flex items-center mb-4">
							<ExclamationTriangleIcon className="h-6 w-6 text-red-600 mr-2" />
							<h3 className="text-lg font-medium text-gray-900">
								Reset Live API Keys
							</h3>
						</div>
						<div className="mb-4">
							<p className="text-sm text-gray-500 mb-4">
								This is a high-security operation. To reset your live API keys,
								please enter your institution ID ({user?.id}) as confirmation.
							</p>
							<input
								type="text"
								value={confirmationCode}
								onChange={(e) => setConfirmationCode(e.target.value)}
								placeholder="Enter your institution ID"
								className="input-field w-full"
							/>
						</div>
						<div className="flex space-x-3">
							<Button
								type="secondary"
								variant="outlined"
								label="Cancel"
								onClick={() => {
									setShowResetLiveModal(false);
									setConfirmationCode('');
								}}
								size="S"
							/>
							<Button
								type="danger"
								label="Reset Live Keys"
								onClick={handleResetLiveKeys}
								loading={actionLoading.resetLive}
								disabled={
									actionLoading.resetLive ||
									confirmationCode !== user?.id?.toString()
								}
								size="S"
							/>
						</div>
					</div>
				</div>
			)}
		</div>
	);
};

export default ApiKeys;
